import styled from '@emotion/styled';
import {
  Fade,
  Menu,
  MenuItem,
  MenuItemProps,
  PopoverOrigin,
} from '@mui/material';
import React, {useMemo, useState} from 'react';

import {ChevronSmallRight} from '@ELib/assets/images';
import {
  mixinFlexboxRow,
  mixinFocusWithBeforeContent,
  mixinSVGIcon,
} from '@ELib/styles/mixins';
import {classy} from '@ELib/utilities/HelperFunctions';
import {useMountEffect} from '@ELib/utilities/hooks/effectHelpers';
import {SetInactiveTextType} from '@ELib/utilities/types';

export type SubMenuActionsType = {
  callback?: () => void;
  id: string;
  text: string;
  section?: number;
  subActions?: SubMenuActionsType[];
  Icon?: React.FunctionComponent;
  inactive?: boolean;
  inactiveAsync?: (setInactiveText: SetInactiveTextType) => Promise<boolean>;
  transformOrigin?: PopoverOrigin;
  anchorOrigin?: PopoverOrigin;
  selected?: boolean;
};

type EMenuProps = {
  disablePortal?: boolean;
  anchorEl: HTMLElement | null;
  setAnchorEl: (anchor: HTMLElement | null) => void;
  subActions?: SubMenuActionsType[];
  transformOrigin?: PopoverOrigin;
  anchorOrigin?: PopoverOrigin;
};

type ItemAlignment = 'left' | 'right';

export const EMenu = ({
  disablePortal,
  anchorEl,
  setAnchorEl,
  subActions,
  transformOrigin = {vertical: 'top', horizontal: 'right'},
  anchorOrigin = {horizontal: 'left', vertical: 'top'},
}: EMenuProps): React.ReactElement => (
  <EMenuList
    anchorEl={anchorEl}
    anchorOrigin={anchorOrigin}
    autoFocus={false}
    disablePortal={disablePortal}
    open={Boolean(anchorEl)}
    PaperProps={{
      className: 'EButton-Menu',
      style: {
        pointerEvents: 'auto',
      },
    }}
    /* Grow enters at scale(0.75, 0.5625), which squashes the 1px section separators. */
    slots={{transition: Fade}}
    transformOrigin={transformOrigin}
    transitionDuration={250}
    hideBackdrop
    onClick={() => setAnchorEl(null)}
    onClose={() => setAnchorEl(null)}
  >
    {subActions?.map((action, idx) => [
      idx !== 0 && subActions[idx - 1]?.section !== action.section ? (
        <SectionSplit
          key={`splitter - ${action.text}`}
          className='EMenu-Section-Split'
        />
      ) : null,
      <EMenuItem
        key={action.text}
        anchorOrigin={action.anchorOrigin}
        className='EButton-Menu-Item'
        data-testid={action.id ? `EMenuItem-${action.id}` : undefined}
        disabled={action.inactive}
        inactiveAsync={action.inactiveAsync}
        selected={action.selected}
        subActions={action.subActions}
        transformOrigin={action.transformOrigin}
        onClick={(e: React.MouseEvent<HTMLLIElement>) => {
          if (action.callback) {
            e.preventDefault();
            e.stopPropagation();
            action.callback();
            setAnchorEl(null);
          }
        }}
        onSubItemSelect={() => setAnchorEl(null)}
      >
        {action.Icon && <action.Icon />}
        {action.text}
      </EMenuItem>,
    ])}
  </EMenuList>
);

export const EMenuItem = ({
  subActions,
  children,
  onSubItemSelect,
  transformOrigin,
  anchorOrigin,
  align = 'left',
  onClick,
  disabled,
  inactiveAsync,
  ...props
}: {
  subActions?: SubMenuActionsType[];
  onSubItemSelect?: () => void;
  transformOrigin?: PopoverOrigin;
  anchorOrigin?: PopoverOrigin;
  align?: ItemAlignment;
  inactiveAsync?: (setInactiveText: SetInactiveTextType) => Promise<boolean>;
} & MenuItemProps): React.ReactElement => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const [inactiveState, setInactiveState] = useState(Boolean(inactiveAsync));

  useMountEffect(() => {
    if (!inactiveAsync) {
      return;
    }
    (async () => {
      const isInactive = await inactiveAsync(() => undefined);
      setInactiveState(isInactive);
    })();
  });

  const menu = useMemo(
    () => (
      <EMenu
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        setAnchorEl={(anchor) => {
          setAnchorEl(anchor);
          onSubItemSelect?.();
        }}
        subActions={subActions}
        transformOrigin={transformOrigin}
        disablePortal
      />
    ),
    [anchorEl, anchorOrigin, onSubItemSelect, subActions, transformOrigin]
  );
  return (
    <Item
      disableRipple
      {...props}
      disabled={disabled || inactiveState}
      onClick={(event) => {
        setAnchorEl(null);
        onClick?.(event);
      }}
      onMouseEnter={
        subActions ? (e) => setAnchorEl(e.currentTarget) : undefined
      }
      onMouseLeave={subActions ? () => setAnchorEl(null) : undefined}
    >
      <ChildWrapper className={classy({alignRight: align === 'right'})}>
        {children}
      </ChildWrapper>
      {subActions ? (
        <>
          <ChevronSmallRight />
          {menu}
        </>
      ) : undefined}
    </Item>
  );
};

const Item = styled(MenuItem)`
  color: var(--color-core-link);
  font-weight: var(--font-weight-thin);
  font-size: var(--font-size-default);

  display: flex;

  &.Mui-focusVisible {
    background-color: transparent;
  }

  &:hover,
  &.Mui-selected:hover {
    background-color: var(--color-state-hover);
    color: var(--color-core-black);
  }

  &:focus,
  &.Mui-selected:focus {
    ${mixinFocusWithBeforeContent}
  }

  &.Mui-selected:focus {
    background-color: var(--color-state-active);
  }

  &.Mui-selected {
    background-color: var(--color-state-active);
    color: var(--color-core-black);
    font-weight: var(--font-weight-small);
  }

  svg {
    ${mixinSVGIcon}
  }

  /* Allow mouse events to go through the backdrop, used for the submenu*/
  .MuiPopover-root {
    pointer-events: none;
  }

  &.Mui-disabled {
    color: var(--input-color-disabled);
  }
`;

/**
 * EMenuList wraps the material ui "Menu" to style the item with our design
 */
export const EMenuList = styled(Menu)`
  & .MuiMenu-paper {
    box-shadow: none;
    border-radius: var(--popover-border-radius);
    border: var(--regular-border-width) solid var(--color-menu-border-focus);
  }
  & .MuiMenu-list {
    padding: 0;
  }
`;

/**
 * Line between sections in an EMenu if sections are given
 */
export const SectionSplit = styled.li`
  height: 0;
  width: 100%;
  /* A border instead of a filled 1px rectangle: browser zoom rounds border
     widths on the same path as the paper's outer border, so the separator
     stays as thick as the surrounding frame at every zoom level. */
  border-bottom: var(--regular-border-width) solid
    var(--color-menu-border-focus);
`;

const ChildWrapper = styled.span`
  ${mixinFlexboxRow}
  flex-grow: 1;
  align-items: center;
  justify-content: flex-start;
  gap: var(--gutter-small);

  &.alignRight {
    justify-content: flex-end;
  }
`;
